//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 06/10/2025
// Version       : 1
// Description   : Agregar componentes para vehículos
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect } from "react";
import { UrlApi } from "../../../../services/apirest";
import {
  TextField,
  Stack,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

// Notification
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AgregarComponente(props) {
  const [datosComponente, setDatosComponente] = useState({
    nombre: null,
    estado: null,
  });

  //Notification
  const [openSnack, setOpenSnack] = React.useState(false);
  const [colormensaje, setColorMensaje] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [severity, setSeverity] = useState("success"); // "success" | "error"

  //*************************************************************************************************************************************/

  // DESHABILITAR BOTON AL GUARDAR
  const [disBtn, setDisBtn] = useState(false);

  //*************************************************************************************************************************************/

  // Función que normaliza texto para comparar nombres
  const normalizarTexto = (texto) => {
    return texto
      ? texto
          .normalize("NFD") // separo letras y acentos
          .replace(/[\u0300-\u036f]/g, "") // elimino acentos
          .trim()
          .toLowerCase()
      : "";
  };

  // --- Verificar si ya existe un "tipo" con ese nombre ---
  const nombreExiste = async (nombre) => {
    if (!nombre) return false;
    const nombreNormalizado = normalizarTexto(nombre);

    try {
      const res = await fetch(`${UrlApi}/vehiculos_componentes`);
      if (!res.ok) return false;
      const data = await res.json();

      // Comparo los nombres normalizados
      if (Array.isArray(data)) {
        return data.some(
          (item) => normalizarTexto(item.nombre) === nombreNormalizado
        );
      }

      // Si devuelve un objeto con propiedad "exists"
      if (typeof data === "object" && "exists" in data) {
        return !!data.exists;
      }

      return false;
    } catch (err) {
      return false;
    }
  };

  //*************************************************************************************************************************************/

  // Función GUARDAR
  const guardarCrear = async (e) => {
    e.preventDefault();

    // Validación mínima: nombre no vacío
    const nombreTrim = (datosComponente.nombre || "").toString().trim();
    if (!nombreTrim) {
      setSeverity("error");
      setColorMensaje("#F1948A");
      setMensaje("El nombre no puede quedar vacío.");
      setOpenSnack(true);
      return;
    }

    // Verificamos existencia antes de intentar POST
    const existe = await nombreExiste(nombreTrim);
    if (existe) {
      setSeverity("error");
      setColorMensaje("#F1948A");
      setMensaje("Ya existe un componente con ese nombre.");
      setOpenSnack(true);
      setDisBtn(false);
      return;
    }

    try {
      const res = await fetch(UrlApi + "/vehiculos_componentes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: datosComponente.nombre,
          estado: 1, // "Componentes" activos por defecto
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setSeverity("success");
        setColorMensaje("#D4EFDF");
        setMensaje("El componente se guardó exitosamente!");
      } else if (res.status === 409) {
        // Nombre duplicado
        setSeverity("error");
        setColorMensaje("#F1948A");
        setMensaje(data.message || "El componente ya existe.");
        setDisBtn(false);
      } else {
        // Otro error
        setSeverity("error");
        setColorMensaje("#F1948A");
        setMensaje(data.error || "Error al guardar el componente.");
        setDisBtn(false);
      }
    } catch (err) {
      setSeverity("error");
      setColorMensaje("#F1948A");
      setMensaje("Error de red o servidor.");
      setDisBtn(false);
    } finally {
      // Muestro el snack en todos los casos
      setOpenSnack(true);
    }
  };

  const almacenar = (e) => {
    const { name, value } = e.target;
    setDatosComponente({
      ...datosComponente,
      [name]: value === "" ? null : value,
    });
  };

  //*************************************************************************************************************************************/

  // Cierre de la notificación
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnack(false);

    if (severity === "success") {
      // Refrescar datos y cerrar diálogo
      props.onComponenteGuardado();
      props.handleCloseAgregarComponente();
      setDisBtn(false);
    }
    // Si fue error, no se cierra el diálogo, solo se reactiva el botón
  };

  return (
    <>
      <Box>
        <DialogTitle
          sx={{
            p: 0,
            mt: 0,
            mb: 3,
            display: "flex",
            justifyContent: "flex-start", 
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="h6"
            color="white"
            sx={{
              background: "linear-gradient(135deg, #2B76A6 0%, #094d77ff 100%)",
              borderBottomRightRadius: "12px",
              padding: "8px 18px",
            }}
          >
            Agregar Componente
          </Typography>
        </DialogTitle>

        <DialogContent>
          <form onSubmit={guardarCrear}>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  required
                  focused
                  size="small"
                  name="nombre"
                  autoComplete="off"
                  fullWidth
                  value={datosComponente.nombre}
                  onChange={almacenar}
                />
              </Grid>

              {/* BOTONES EN LÍNEA */}
              <Grid item xs={12} mt={3}>
                <Box display="flex" justifyContent="space-between">
                  <Button
                    variant="contained"
                    color="error"
                    onClick={props.handleCloseAgregarComponente}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Volver
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={disBtn}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Guardar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Box>

      {/* Notificación al Guardar */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={1500}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnack}
            severity={severity}
            sx={{ width: "100%" }}
            style={{ backgroundColor: colormensaje, color: "black" }}
          >
            {mensaje}
          </Alert>
        </Snackbar>
      </Stack>
    </>
  );
}

export default AgregarComponente;
